import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtVerify } from 'jose';
import localforage from 'localforage';
import { usePharmacy } from '../PharmacyProvider';
import { useNavigate } from 'react-router';

const LicenseContext = createContext();

localforage.config({
    name: 'ClinicDotsPharmacy',
    storeName: 'license_store',
    description: 'Persistent license storage'
});

const TOKEN_KEY = 'license_token';
const LAST_VALID_TIME_KEY = 'license_last_valid_time';

const PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA6GACe0S3bwEoIcfLU1bu
KkJpBUMITjAcKtyHMCuzKoke3/6865P1HpYeOdFMi57ythD3aVdoA3MhrWXoFBvh
9e31WXeI52zLT2H5myciq4Byne0p+M2JQr7hn8WeOF820is30z4p45pPKQC/yEQ0
DEKC0YwnYVzPYCo0As6xdGiIhjh14dC7yWN1KWYxhWBCEJ5uuP4taamV2XCHbbxX
XGOihNC2ISkxRdUqelc0dHoidt2r6+I2orIEnblvwKfBb2QUtL5lZsWm5WNamirR
jOOniVLJ+6a1kG5tIo7rSY/hCgB2ZfFx9gWLm9WAjt+06SvtXCb97xXf1LfkmAxj
DwIDAQAB
-----END PUBLIC KEY-----`;

export const LicenseProvider = ({ children }) => {
    const history = useNavigate();
    const [licenseStatus, setLicenseStatus] = useState('checking');
    const [licenseData, setLicenseData] = useState(null);
    const [isChecking, setIsChecking] = useState(true);
    const [skipValidation, setSkipValidationState] = useState(false);
    const { pharmacyPOS } = usePharmacy();

    // Store the user ID to detect actual changes
    const previousUserIdRef = useRef(null);
    const skipValidationTimeoutRef = useRef(null);
    const skipValidationReasonRef = useRef(null);

    const getPublicKey = async () => {
        const pem = PUBLIC_KEY_PEM
            .replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\n/g, '')
            .replace(/\s+/g, '');
        const binaryDer = Uint8Array.from(atob(pem), c => c.charCodeAt(0));
        return await crypto.subtle.importKey(
            'spki',
            binaryDer.buffer,
            { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
            true,
            ['verify']
        );
    };

    const validateToken = async (token) => {
        try {
            const key = await getPublicKey();
            const { payload } = await jwtVerify(token.trim(), key, { algorithms: ['RS256'] });

            const now = Date.now();

            let expiryTime;
            if (payload.expiresAt) {
                expiryTime = typeof payload.expiresAt === 'string'
                    ? parseInt(payload.expiresAt)
                    : payload.expiresAt;
            } else if (payload.exp) {
                expiryTime = payload.exp * 1000;
            }

            if (!expiryTime) {
                setLicenseStatus('invalid');
                setIsChecking(false);
                return;
            }

            let activationTime;
            if (payload.activationDate) {
                activationTime = new Date(payload.activationDate).getTime();
            }

            const lastValidTime = await localforage.getItem(LAST_VALID_TIME_KEY);
            const storedInstallTime = await localforage.getItem('license_install_time');

            if (!storedInstallTime) {
                if (activationTime) {
                    await localforage.setItem('license_install_time', activationTime.toString());
                } else {
                    await localforage.setItem('license_install_time', now.toString());
                }
            } else {
                const installTimestamp = parseInt(storedInstallTime);

                if (activationTime && now < activationTime - 86400000) {
                    console.error('System time set before license activation date!', {
                        systemTime: new Date(now).toISOString(),
                        activationDate: new Date(activationTime).toISOString()
                    });
                    setLicenseStatus('time-tampered');
                    setLicenseData(payload);
                    setIsChecking(false);
                    return;
                }

                if (now < installTimestamp - 86400000) {
                    setLicenseStatus('time-tampered');
                    setLicenseData(payload);
                    setIsChecking(false);
                    return;
                }
            }

            if (lastValidTime) {
                const lastValid = parseInt(lastValidTime);
                if (now < lastValid - 300000) {
                    console.error('System time tampered detected!', {
                        lastValidTime: new Date(lastValid).toISOString(),
                        currentTime: new Date(now).toISOString(),
                        difference: lastValid - now
                    });
                    setLicenseStatus('time-tampered');
                    setLicenseData(payload);
                    setIsChecking(false);
                    return;
                }
            }

            if (expiryTime < now) {
                setLicenseStatus('expired');
                // clearLicense();
                setLicenseData(payload);
                setIsChecking(false);
                return;
            }

            await localforage.setItem(LAST_VALID_TIME_KEY, now.toString());
            setLicenseData(payload);
            setLicenseStatus('valid');
            setIsChecking(false);

        } catch (err) {
            console.error('License verification failed:', err.message);
            setLicenseStatus('invalid');
            setIsChecking(false);
        }
    };

    const decodeTokenOnly = async () => {
        const token = await localforage.getItem(TOKEN_KEY);
        if (!token) return null;
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        return JSON.parse(jsonPayload);
    };

    const doesLicenseBelongToUser = (payload, userId) => {
        if (!payload || !userId) return false;

        if (Array.isArray(payload.users)) {
            if (payload.users.some(u => u.id === userId)) return true;
        }

        if (Array.isArray(payload.subUserIds)) {
            if (payload.subUserIds.includes(userId)) return true;
        }

        return false;
    };

    const performValidation = async () => {
        if (!pharmacyPOS || !pharmacyPOS._id || skipValidation) {
            return;
        }

        try {
            const decodedPayload = await decodeTokenOnly();

            if (!decodedPayload) {
                setLicenseStatus('none');
                setIsChecking(false);
                return;
            }

            const userBelongs = doesLicenseBelongToUser(decodedPayload, pharmacyPOS._id);

            if (!userBelongs) {
                setLicenseStatus('none');
                setIsChecking(false);
                return;
            }

            const token = await localforage.getItem(TOKEN_KEY);
            if (!token) {
                setLicenseStatus('none');
                setIsChecking(false);
                return;
            }

            await validateToken(token);
        } catch (err) {
            console.error('License check error:', err);
            setLicenseStatus('invalid');
            setIsChecking(false);
        }
    };

    // Initial validation when pharmacyPOS changes - but only if ID actually changed
    useEffect(() => {
        if (!pharmacyPOS || !pharmacyPOS._id) {
            setIsChecking(false);
            setLicenseStatus('none');
            previousUserIdRef.current = null;
            return;
        }

        // If user ID hasn't actually changed, skip validation
        if (previousUserIdRef.current === pharmacyPOS._id && !skipValidation) {
            return;
        }

        // Update the stored user ID
        previousUserIdRef.current = pharmacyPOS._id;

        if (skipValidation) {
            // console.log(`⏸️ Skipping validation - ${skipValidationReasonRef.current}`);
            return;
        }

        // console.log('✅ Performing validation for user:', pharmacyPOS._id);
        performValidation();
    }, [pharmacyPOS?._id, skipValidation]);

    // Continuous validation every 5 seconds - ONLY when skipValidation is false
    useEffect(() => {
        if (!pharmacyPOS || !pharmacyPOS._id || skipValidation) {
            return;
        }

        // Perform initial validation
        performValidation();

        // Set up interval
        const interval = setInterval(() => {
            performValidation();
        }, 5000);

        // Also validate on window focus
        const onFocus = () => performValidation();
        window.addEventListener('focus', onFocus);

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [pharmacyPOS?._id, skipValidation]);

    const saveLicenseToken = async (token) => {
        await localforage.setItem(TOKEN_KEY, token.trim());
        await validateToken(token.trim());
    };

    const clearLicense = async () => {
        await localforage.removeItem(TOKEN_KEY);
        await localforage.removeItem(LAST_VALID_TIME_KEY);
        await localforage.removeItem('license_install_time');
        setLicenseStatus('none');
        setLicenseData(null);
        setIsChecking(false);
    };

    // Safe setSkipValidation with longer timeout
    const setSkipValidation = (skip, reason = 'unknown') => {
        // Clear any existing timeout
        if (skipValidationTimeoutRef.current) {
            clearTimeout(skipValidationTimeoutRef.current);
        }

        skipValidationReasonRef.current = reason;
        setSkipValidationState(skip);

        if (skip) {
            // console.log(`⏸️ SKIPPING validation: ${reason}`);
            // Auto-disable skip validation after 5 seconds (increased from 2)
            skipValidationTimeoutRef.current = setTimeout(() => {
                // console.log('✅ RE-ENABLING validation after profile update');
                setSkipValidationState(false);
                skipValidationReasonRef.current = null;
            }, 5000);
        }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (skipValidationTimeoutRef.current) {
                clearTimeout(skipValidationTimeoutRef.current);
            }
        };
    }, []);

    const decodeJWT = (token) => {
        try {
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                    .join("")
            );

            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error("Invalid JWT", error);
            return null;
        }
    };

    return (
        <LicenseContext.Provider value={{
            licenseStatus,
            licenseData,
            isValid: licenseStatus === 'valid',
            isChecking,
            needsActivation: ['none', 'invalid', 'expired', 'time-tampered'].includes(licenseStatus),
            saveLicenseToken,
            clearLicense,
            decodeTokenOnly,
            doesLicenseBelongToUser,
            setSkipValidation,
            decodeJWT
        }}>
            {children}
        </LicenseContext.Provider>
    );
};

export const useLicense = () => useContext(LicenseContext);